/**
 * Service types aligned with Admin API v1.0 contract.
 * @see contracts/openapi-admin-api-v1.0-draft.yaml
 */

import i18n from '@/i18n';

/**
 * Theme for visual customization of payment pages.
 */
export interface ServiceTheme {
  /**
   * Button text variant for legal compliance.
   * 1 = "Zahlungspflichtig bestellen"
   * 2 = "Kostenpflichtig bestellen"
   * 3 = "Kaufen"
   * 4 = "Zahlungspflichtig Vertrag abschließen"
   */
  buttonTextId?: 1 | 2 | 3 | 4;
  /** HTTPS URL to CSS file (max 500 KB) */
  stylesheetUrl?: string;
  /** HTTPS URL to header image (max 500 KB) */
  headerImageUrl?: string;
  /** HTTPS URL to background image (max 500 KB) */
  backgroundImageUrl?: string;
  /** Text shown below CTA button on payment page (max 200 chars) */
  benefitText?: string;
  /** Carrier compliance logo ID (1-4, 10-11) */
  complianceCarrierLogoId?: number;
}

/**
 * Legal links for payment page compliance (Spec 0040).
 */
export interface LegalLinks {
  agbUrl?: string;
  withdrawalUrl?: string;
  imprintUrl?: string;
  privacyUrl?: string;
  supportUrl?: string;
}

/**
 * Subscription interval as ISO 8601 duration.
 */
export type SubscriptionInterval = 'P1D' | 'P1W' | 'P1M' | 'P3M' | 'P6M' | 'P1Y';

/**
 * Service status.
 */
export type ServiceStatus = 'active' | 'inactive';

// Spec 0024: DataSourceMode removed (per-service routing eliminated)

/**
 * Landing-Page Renderer (Spec 0087). Top-Level on Service, not in Theme.
 */
export const THEME_LAYOUTS = ['default', 'gameloft', 'minimal'] as const;
export type ThemeLayout = (typeof THEME_LAYOUTS)[number];

/**
 * Button text variant for legal compliance.
 */
export type ButtonTextId = 1 | 2 | 3 | 4;

/**
 * Payment flow type for gateway routing.
 */
export type FlowType = 'WAP_CLICK' | 'WEB_TAN';

/**
 * Single entry in a fallback chain (Spec 0128).
 * Ordered by priority — first entry is the primary flow.
 */
export interface FallbackChainEntry {
  /** Payment flow type */
  flowType: FlowType;
  /** InternetQ gateway service ID, unique within the chain */
  gatewayServiceId: string;
  /** Optional IQ-Service credentials override */
  iqServiceId?: string;
}

/**
 * Service entity representing a billable subscription product.
 */
export interface Service {
  /** Generated service ID (pattern: svc_...) */
  serviceId: string;
  /** Merchant identifier (lowercase alphanumeric + hyphens, 4-50 chars) */
  merchantId: string;
  /** InternetQ gateway service ID (pattern: iq_svc_...) */
  gatewayServiceId: string;
  /** Reference to Facade service (Admin-only field) */
  facadeServiceId?: string | null;
  /** Service description shown to users (max 200 chars) */
  description: string;
  /** Amount in cents (e.g., 499 for €4.99) */
  grossAmount: number;
  /** Currency code */
  currency: 'EUROCENT';
  /** ISO 8601 duration */
  subscriptionInterval: SubscriptionInterval;
  /** Service status */
  status: ServiceStatus;
  /** Visual customization for payment pages */
  theme?: ServiceTheme;
  /** Asset pipeline status from Facade (Spec 0032 M-6) */
  themeStatus?: 'provisioning' | 'ready' | 'failed';
  /** Landing-Page Renderer (Spec 0087, Top-Level) */
  themeLayout?: ThemeLayout;
  /** Legal links for payment page (Spec 0040 M-4) */
  legalLinks?: LegalLinks;
  /** Approval status for legal links (Spec 0040 M-4) */
  legalLinksStatus?: 'pending_review' | 'approved' | 'rejected';
  /** Ordered fallback chain with 1-5 payment flow entries (Spec 0128) */
  fallbackChain?: FallbackChainEntry[];
  /** Merchant-eigene MSISDN-Erfassungs-URL, null = Facade-Standard (Spec 0128) */
  msisdnCollectionUrl?: string | null;
  /** Creation timestamp */
  createdAt: string;
  /** Last update timestamp */
  updatedAt: string;
  /** Last sync with Facade API */
  syncedAt?: string | null;
}

/**
 * Request to create a new service.
 */
export interface CreateServiceRequest {
  /** Merchant identifier (lowercase alphanumeric + hyphens, 4-50 chars) */
  merchantId: string;
  /** InternetQ gateway service ID — deprecated, use fallbackChain (Spec 0128) */
  gatewayServiceId?: string;
  /** Service description shown to users (max 200 chars) */
  description: string;
  /** Amount in cents (e.g., 499 for €4.99) */
  grossAmount: number;
  /** Currency code */
  currency: 'EUROCENT';
  /** ISO 8601 duration */
  subscriptionInterval: SubscriptionInterval;
  /** Visual customization for payment pages */
  theme?: ServiceTheme;
  /** Landing-Page Renderer (Spec 0087) */
  themeLayout?: ThemeLayout;
  /** Legal links for payment page (Spec 0040) */
  legalLinks?: LegalLinks;
  /** Ordered fallback chain — replaces single gatewayServiceId (Spec 0128) */
  fallbackChain?: FallbackChainEntry[];
  /** Merchant-eigene MSISDN-Erfassungs-URL (Spec 0128) */
  msisdnCollectionUrl?: string;
}

/**
 * Request to update an existing service.
 */
export interface UpdateServiceRequest {
  /** Service description shown to users (max 200 chars) */
  description?: string;
  /** Amount in cents (e.g., 499 for €4.99) */
  grossAmount?: number;
  /** ISO 8601 duration */
  subscriptionInterval?: SubscriptionInterval;
  /** Service status (setting to 'inactive' is a soft-delete) */
  status?: ServiceStatus;
  /** Visual customization for payment pages */
  theme?: ServiceTheme;
  /** Landing-Page Renderer (Spec 0087) */
  themeLayout?: ThemeLayout;
  /** Legal links for payment page (Spec 0040) */
  legalLinks?: LegalLinks;
  /** Approval status update (Spec 0040) */
  legalLinksStatus?: 'approved' | 'rejected';
  /** Ordered fallback chain (Spec 0128) */
  fallbackChain?: FallbackChainEntry[];
  /** Merchant-eigene MSISDN-Erfassungs-URL, null = entfernen (Spec 0128) */
  msisdnCollectionUrl?: string | null;
}

/**
 * Pagination info from API responses.
 */
export interface Pagination {
  /** Cursor for next page, null if last page */
  nextCursor?: string | null;
  /** Whether more items exist */
  hasMore: boolean;
  /** Items per page */
  limit: number;
}

/**
 * API response for listing services.
 */
export interface ServiceListResponse {
  data: Service[];
  pagination: Pagination;
}

/**
 * Parameters for listing services.
 */
export interface ListServicesParams {
  /** Pagination cursor from previous response */
  cursor?: string;
  /** Number of items to return (1-100, default 20) */
  limit?: number;
  /** Field to sort by */
  sortBy?: 'createdAt' | 'updatedAt' | 'name' | 'description';
  /** Sort direction */
  sortDir?: 'asc' | 'desc';
  /** Filter by status */
  status?: 'active' | 'inactive' | 'all';
  /** Filter by merchant ID */
  merchantId?: string;
  /** Filter by gateway service ID */
  gatewayServiceId?: string;
}

// SUBSCRIPTION_INTERVAL_LABELS, INTERVAL_LABEL_SHORT, and BUTTON_TEXT_LABELS
// have been migrated to i18n translation files (services namespace).
// Use t('services:interval.P1D'), t('services:intervalShort.P1D'),
// and t('services:buttonText.1') respectively.

/**
 * Status of an external asset (image, stylesheet).
 */
export type AssetStatus =
  | { status: 'empty' }
  | { status: 'loading' }
  | { status: 'success'; size: number; url: string }
  | { status: 'error'; message: string };

/**
 * Validation status for all theme assets.
 */
export interface AssetValidationStatus {
  headerImage: AssetStatus;
  backgroundImage: AssetStatus;
  stylesheet: AssetStatus;
}

/**
 * Default empty asset validation status.
 */
export const EMPTY_ASSET_STATUS: AssetValidationStatus = {
  headerImage: { status: 'empty' },
  backgroundImage: { status: 'empty' },
  stylesheet: { status: 'empty' },
};

/**
 * Format price in cents to EUR display string, respecting the active locale.
 */
export function formatPrice(cents: number): string {
  const lang = i18n.language?.substring(0, 2) || 'en';
  const locale = lang === 'de' ? 'de-DE' : 'en-GB';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}
